import { useEffect, useState } from "react";
import {
  FOLLOW_MODAL_TITLE,
  FOLLOW_MODAL_WHAT,
  FOLLOW_MY_VOICE,
} from "./green-room.js";
import { Modal } from "./Modal.js";
import { useScrollMode } from "./scroll-link.js";
import {
  followBox,
  followModal,
  modelCommand,
  useModelStatus,
} from "./model-download.js";

/**
 * `Follow my voice`, in the green room (flow ticket 06).
 *
 * The feature this product exists for, given a name and a switch. Before this it
 * was two things that were not a person deciding anything: a panel offering a
 * 64 MB download with one sentence of framing, and an unpersisted enum reached
 * through a `⋯` row called "Cycle mode".
 *
 * **The box is the download's entry point now**, which is the one thing here that
 * changes an existing screen rather than adding to it. `ModelPanel` keeps every
 * other phase — the bar, the failure, the two ways out of one — and sits under
 * this, so what a visitor sees is a switch with its progress beneath it rather
 * than an offer and a switch making the same offer twice.
 *
 * Everything it decides is in `model-download.ts` beside the phases, and its
 * words are in `green-room.ts` with the room's. What is here is the wiring: two
 * subscriptions, one dialog, and which contract call each press makes.
 */
export function FollowVoice(): JSX.Element {
  const status = useModelStatus();
  const mode = useScrollMode();
  const [asking, setAsking] = useState(false);

  const box = followBox({ status, mode });
  const dialog = asking ? followModal(status) : null;

  /**
   * The question answering itself.
   *
   * The dialog can be opened on `checking` — a fraction of a second in which the
   * honest answer is "finding out" — and the manifest can land on `ready` under
   * it, or another surface can start the download while it is open. All of those
   * leave nothing to agree to, and the agreement is what the dialog was for, so
   * it closes and the box goes on. Anything else would be a visitor pressing
   * **Download** to be told the thing had already downloaded.
   *
   * **Except `unavailable`, which is also nothing to agree to and is the
   * opposite answer.** The manifest can land on "there is no model here at all",
   * and turning speech-following on then would put the prompter in a mode it can
   * never deliver — the one thing goal G5 forbids. The dialog closes, the box
   * stays where it was, and the box itself goes dead and says why.
   */
  useEffect(() => {
    if (!asking || followModal(status) !== null) return;
    setAsking(false);
    if (status.phase !== "unavailable") turnOn();
  }, [asking, status]);

  return (
    <section className="web-follow">
      {/* The repository's one checkbox pattern: a native input inside its label,
        * the words as a sibling. No toggle component is added for this. */}
      <label className="web-follow-toggle">
        <input
          type="checkbox"
          checked={box.on}
          disabled={box.disabled}
          onChange={(event) => {
            if (!event.target.checked) {
              turnOff();
              return;
            }
            if (box.asks) setAsking(true);
            else turnOn();
          }}
        />
        {FOLLOW_MY_VOICE}
      </label>
      {/* The reason `unavailable` refuses it, where a title is the only place a
        * dead checkbox has to say anything — the same trick the room's start
        * button uses for its own refusal. */}
      {box.reason !== undefined && (
        <p className="web-follow-reason" role="status">
          {box.reason}
        </p>
      )}

      {dialog !== null && (
        <Modal
          title={FOLLOW_MODAL_TITLE}
          onClose={() => setAsking(false)}
          buttons={
            <>
              <button
                type="button"
                className="web-modal-cancel"
                onClick={() => setAsking(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="web-modal-go"
                // Dead until the live phase would accept a `start`. A confirm
                // fired from any other one is a silent no-op in the loader,
                // which is a button that lies rather than a button that fails.
                disabled={dialog.confirm === undefined}
                onClick={() => {
                  setAsking(false);
                  turnOn();
                  // Through `modelCommand` and never the port: that is what asks
                  // this browser for persistent storage first, and the ordering
                  // is structural rather than remembered.
                  if (dialog.confirm !== undefined)
                    modelCommand(dialog.confirm.command);
                }}
              >
                {dialog.confirm?.label ?? "Download"}
              </button>
            </>
          }
        >
          <p className="web-follow-what">{FOLLOW_MODAL_WHAT}</p>
          {/* The cost, in the panel's own words. Never a figure written here:
            * what it quotes is the bytes still absent, which a resumed download
            * makes smaller. */}
          <p className="web-follow-cost">{dialog.says}</p>
        </Modal>
      )}
    </section>
  );
}

/**
 * On, and remembered.
 *
 * One call, because on this host naming a mode *is* the decision being recorded —
 * see the handler in `core-host.ts`. The `⋯` menu's cycle writes nothing, which
 * is the difference between somebody deciding and somebody mid-take reaching for
 * the next mode.
 *
 * Exported for the prompter's Download button, which is the same decision made
 * from the other screen.
 */
export function turnOn(): void {
  window.cuekards.setScrollMode("speech");
}

/**
 * Off, and remembered.
 *
 * `constant` and not `manual`: the conventional teleprompter FR-35 keeps as the
 * floor (goal G5). A script that never moves on its own is what somebody who has
 * just switched a feature *off* would read as broken. Nothing is deleted — a
 * model already on this device stays on it.
 */
function turnOff(): void {
  window.cuekards.setScrollMode("constant");
}
